import { Button, DropdownMenu, Drawer, Input, Prompt, toast } from "@medusajs/ui"
import { EllipsisHorizontal } from "@medusajs/icons"
import { useMemo, useState } from "react"
import { duplicateById, useBulkProducts, useCollections, useProductTags, useProductTypes, useStoreContext, type BulkAction } from "@/lib/catalog"
import { FilterBar, FilterSelect } from "@/terabizz"
import { useColumns, ViewTabs, type FixedTab } from "@/components/list-views"
import { formatDate } from "@/lib/format"
import { productUrl } from "@/lib/storefront-url"
import { useQueryClient } from "@tanstack/react-query"
import { errorMessage } from "@/lib/sdk"
import { Field } from "@/components/form"
import { Link } from "react-router-dom"
import { useNavigate, useSearchParams } from "react-router-dom"
import { ProductImportDialog } from "@/components/product-import"
import { downloadExport } from "@/lib/export"
import { DataTable, ErrorState, PageHeader, SearchInput, Section, Status, Thumb, usePage } from "@/components/ui"
import { useProducts } from "@/lib/api"
import { useCan } from "@/lib/permissions"

const PAGE = 25
const TABS: FixedTab[] = [
  { value: "all", label: "All", params: {} },
  { value: "published", label: "Active", params: { status: "published" } },
  { value: "draft", label: "Draft", params: { status: "draft" } },
  { value: "archived", label: "Archived", params: { status: "archived" } },
]
const SORTS: [string, string][] = [
  ["title", "Title A–Z"],
  ["-title", "Title Z–A"],
  ["-created_at", "Newest created"],
  ["created_at", "Oldest created"],
  ["-updated_at", "Recently updated"],
]
const OPTIONAL_COLUMNS = [
  { key: "status", label: "Status" },
  { key: "inventory", label: "Inventory" },
  { key: "type", label: "Product type" },
  { key: "vendor", label: "Vendor" },
  { key: "collection", label: "Collection", default: false },
  { key: "tags", label: "Tags", default: false },
  { key: "variants", label: "Variants", default: false },
  { key: "updated", label: "Updated", default: false },
  { key: "created", label: "Created", default: false },
]

export function ProductsPage() {
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const status = params.get("status") ?? ""
  // Archived = draft + metadata.archived (Medusa has no archived status), so those tabs filter client-side over a wider window.
  const wide = status === "archived" || status === "draft"
  const f = (k: string) => params.get(k) ?? ""
  const facets = { type_id: f("type"), collection_id: f("collection"), tag_id: f("tag"), order: f("sort") }
  const { data: raw, isLoading, error } = useProducts({ q, status: status === "archived" ? "draft" : status || undefined, ...Object.fromEntries(Object.entries(facets).filter(([, v]) => v)), limit: wide ? 200 : PAGE, offset: wide ? 0 : pageIndex * PAGE })
  const types = useProductTypes().data?.product_types ?? []
  const collections = useCollections().data?.collections ?? []
  const tags = useProductTags().data?.product_tags ?? []
  const cols = useColumns("products", OPTIONAL_COLUMNS)
  const setFacet = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) v && !(k === "sort" && v === "title") ? next.set(k, v) : next.delete(k)
    next.delete("page")
    setSelected(new Set())
    setParams(next)
  }
  const filtered = !!(facets.type_id || facets.collection_id || facets.tag_id)
  const data = useMemo(() => {
    if (!raw || !wide) return raw
    const list = raw.products.filter((p: any) => (status === "archived") === !!(p.metadata as any)?.archived)
    return { ...raw, products: list.slice(pageIndex * PAGE, pageIndex * PAGE + PAGE), count: list.length }
  }, [raw, wide, status, pageIndex])
  const navigate = useNavigate()
  const can = useCan()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [tagMode, setTagMode] = useState<"add" | "remove" | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [importing, setImporting] = useState(false)
  const bulk = useBulkProducts()
  const ctx = useStoreContext().data
  const qc = useQueryClient()
  const duplicate = async (p: any) => {
    const t = toast.loading(`Duplicating ${p.title}…`)
    try {
      const r = await duplicateById(p.id, { salesChannelId: ctx?.salesChannelId, locationId: ctx?.locationId, shippingProfileId: ctx?.shippingProfileId })
      toast.dismiss(t)
      toast.success("Product duplicated as a draft")
      qc.invalidateQueries({ queryKey: ["products"] })
      navigate(`/products/${r.id}`)
    } catch (e) {
      toast.dismiss(t)
      toast.error(errorMessage(e))
    }
  }
  const rowsSel = () => (data?.products ?? []).filter((p: any) => selected.has(p.id))
  const run = (action: BulkAction, msg: string) =>
    bulk.mutate(
      { rows: rowsSel(), action },
      { onSuccess: () => (toast.success(msg), setSelected(new Set())), onError: (e) => toast.error(errorMessage(e)) }
    )

  return (
    <>
      <PageHeader
        title="Products"
        actions={
          <>
            {can("products:write") && <Button size="small" variant="secondary" onClick={() => setImporting(true)}>Import</Button>}
            <Button size="small" variant="secondary" onClick={() => downloadExport("/admin/terify/products/export")}>Export</Button>
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button size="small" variant="secondary">More actions</Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                {can("inventory:read") && <DropdownMenu.Item onClick={() => navigate("/inventory")}>Manage inventory</DropdownMenu.Item>}
                <DropdownMenu.Item onClick={() => navigate("/collections")}>Manage collections</DropdownMenu.Item>
                {can("gift_cards:read") && <DropdownMenu.Item onClick={() => navigate("/products/gift-cards")}>Manage gift cards</DropdownMenu.Item>}
                {can("products:write") && (
                  <>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item onClick={() => setImporting(true)}>Import products</DropdownMenu.Item>
                  </>
                )}
              </DropdownMenu.Content>
            </DropdownMenu>
            {can("products:write") && (
              <Button size="small" asChild>
                <Link to="/products/new">Add product</Link>
              </Button>
            )}
          </>
        }
      />
      <Section>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <ViewTabs resource="products" tabs={TABS} params={params} setParams={(p) => (setSelected(new Set()), setParams(p))} canSave={can("products:write")} />
          <div className="flex items-center gap-2">
            <SearchInput value={q} onChange={setQ} placeholder="Search products" />
            {cols.menu}
          </div>
        </div>
        <FilterBar>
          <FilterSelect label="Product type" value={facets.type_id} onChange={(v) => setFacet({ type: v })} options={types.map((t: any) => [t.id, t.value] as [string, string])} />
          <FilterSelect label="Collection" value={facets.collection_id} onChange={(v) => setFacet({ collection: v })} options={collections.map((c: any) => [c.id, c.title] as [string, string])} />
          <FilterSelect label="Tag" value={facets.tag_id} onChange={(v) => setFacet({ tag: v })} options={tags.map((t: any) => [t.id, t.value] as [string, string])} />
          {filtered && <Button size="small" variant="transparent" onClick={() => setFacet({ type: "", collection: "", tag: "" })}>Clear filters</Button>}
          <div className="ml-auto"><FilterSelect label="Sort" value={facets.order || "title"} onChange={(v) => setFacet({ sort: v })} allowAll={false} options={SORTS} /></div>
        </FilterBar>
        {error ? (
          <ErrorState error={error} />
        ) : (
          <DataTable
            rowKey={(p) => p.id}
            rows={data?.products}
            count={data?.count ?? 0}
            pageSize={PAGE}
            pageIndex={pageIndex}
            onPageChange={setPage}
            loading={isLoading}
            onRowClick={(p) => navigate(`/products/${p.id}`)}
            selected={can("products:write") ? selected : undefined}
            onSelectedChange={can("products:write") ? setSelected : undefined}
            bulkActions={
              <>
                <Button size="small" variant="secondary" onClick={() => navigate(`/products/bulk-edit?ids=${[...selected].join(",")}`)}>Bulk edit</Button>
                <Button size="small" variant="secondary" isLoading={bulk.isPending} onClick={() => run({ kind: "status", status: "published" }, `${selected.size} product${selected.size === 1 ? "" : "s"} set as active`)}>Set as active</Button>
                <Button size="small" variant="secondary" isLoading={bulk.isPending} onClick={() => run({ kind: "status", status: "draft" }, `${selected.size} product${selected.size === 1 ? "" : "s"} set as draft`)}>Set as draft</Button>
                <DropdownMenu>
                  <DropdownMenu.Trigger asChild>
                    <Button size="small" variant="secondary" aria-label="More bulk actions"><EllipsisHorizontal /></Button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="end">
                    <DropdownMenu.Item onClick={() => run({ kind: "archive", archived: true }, "Products archived")}>Archive products</DropdownMenu.Item>
                    <DropdownMenu.Item onClick={() => run({ kind: "archive", archived: false }, "Products unarchived")}>Unarchive products</DropdownMenu.Item>
                    <DropdownMenu.Item onClick={() => setTagMode("add")}>Add tags</DropdownMenu.Item>
                    <DropdownMenu.Item onClick={() => setTagMode("remove")}>Remove tags</DropdownMenu.Item>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item className="text-ui-fg-error" onClick={() => setConfirmDelete(true)}>Delete products</DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu>
              </>
            }
            mobile={(p) => (
              <div className="flex items-start gap-x-3">
                <Thumb src={p.thumbnail} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-x-2">
                    <span className="text-[13px] font-medium">{p.title}</span>
                    <Status value={(p.metadata as any)?.archived ? "archived" : p.status} />
                  </div>
                  <div className="mt-0.5 text-[13px]">{inventoryLabel(p)}</div>
                  {p.type?.value && <div className="text-[12px] text-ui-fg-muted">{p.type.value}</div>}
                </div>
              </div>
            )}
            columns={[
              {
                key: "title",
                header: "Product",
                cell: (p) => (
                  <div className="flex items-center gap-x-3">
                    <Thumb src={p.thumbnail} size={36} />
                    <span className="text-[13px] font-medium">{p.title}</span>
                  </div>
                ),
              },
              ...[
                { key: "status", header: "Status", cell: (p: any) => <Status value={(p.metadata as any)?.archived ? "archived" : p.status} /> },
                { key: "inventory", header: "Inventory", cell: (p: any) => inventoryLabel(p) },
                { key: "type", header: "Product type", cell: (p: any) => p.type?.value ?? "" },
                { key: "vendor", header: "Vendor", cell: (p: any) => (p.metadata as any)?.vendor ?? "" },
                { key: "collection", header: "Collection", cell: (p: any) => p.collection?.title ?? "" },
                { key: "tags", header: "Tags", cell: (p: any) => <span className="text-ui-fg-subtle">{(p.tags ?? []).map((t: any) => t.value).join(", ")}</span> },
                { key: "variants", header: "Variants", cell: (p: any) => (p.variants ?? []).length },
                { key: "updated", header: "Updated", cell: (p: any) => formatDate(p.updated_at) },
                { key: "created", header: "Created", cell: (p: any) => formatDate(p.created_at) },
              ].filter((c) => cols.isOn(c.key)),
              {
                key: "actions",
                header: <span className="sr-only">Actions</span>,
                className: "w-[72px]",
                cell: (p) => (
                  <RowActions
                    product={p}
                    onDuplicate={can("products:write") ? () => duplicate(p) : undefined}
                    onArchive={can("products:write") ? () => bulk.mutate({ rows: [p], action: { kind: "archive", archived: !(p.metadata as any)?.archived } }, { onSuccess: () => toast.success((p.metadata as any)?.archived ? "Product unarchived" : "Product archived"), onError: (e) => toast.error(errorMessage(e)) }) : undefined}
                  />
                ),
              },
            ]}
          />
        )}
      </Section>
      {importing && <ProductImportDialog onClose={() => setImporting(false)} />}
      {tagMode && <TagsDrawer mode={tagMode} count={selected.size} busy={bulk.isPending} onClose={() => setTagMode(null)} onApply={(tags) => (run(tagMode === "add" ? { kind: "tags", add: tags } : { kind: "tags", remove: tags }, tagMode === "add" ? "Tags added" : "Tags removed"), setTagMode(null))} />}
      <Prompt open={confirmDelete} onOpenChange={setConfirmDelete}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {selected.size} product{selected.size === 1 ? "" : "s"}?</Prompt.Title>
            <Prompt.Description>This can't be undone. Orders that include these products keep their line items.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action onClick={() => run({ kind: "delete" }, "Products deleted")}>Delete</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </>
  )
}

/** Row quick actions, like Shopify's eye icon and row menu: preview on the store, duplicate, archive. */
function RowActions({ product: p, onDuplicate, onArchive }: { product: any; onDuplicate?: () => void; onArchive?: () => void }) {
  const live = p.status === "published" && !(p.metadata as any)?.archived && p.handle
  const stop = (e: React.SyntheticEvent) => e.stopPropagation()
  return (
    <div className="flex items-center justify-end gap-0.5" onClick={stop} onKeyDown={stop}>
      {live ? (
        <a
          href={productUrl(p.handle)}
          target="_blank"
          rel="noreferrer"
          aria-label={`Preview ${p.title} on your store`}
          title="Preview on your store"
          className="flex h-7 w-7 items-center justify-center rounded-[8px] text-[var(--tb-ink-3,#616161)] hover:bg-black/5 hover:text-[var(--tb-ink,#303030)]"
        >
          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M1.75 10s3-5.75 8.25-5.75S18.25 10 18.25 10 15.25 15.75 10 15.75 1.75 10 1.75 10Z" /><circle cx="10" cy="10" r="2.5" /></svg>
        </a>
      ) : (
        <span className="h-7 w-7" />
      )}
      {(onDuplicate || onArchive) && (
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <button type="button" aria-label={`More actions for ${p.title}`} className="flex h-7 w-7 items-center justify-center rounded-[8px] text-[var(--tb-ink-3,#616161)] hover:bg-black/5 hover:text-[var(--tb-ink,#303030)]">
              <EllipsisHorizontal />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end" onClick={stop}>
            {onDuplicate && <DropdownMenu.Item onClick={onDuplicate}>Duplicate</DropdownMenu.Item>}
            {onArchive && <DropdownMenu.Item onClick={onArchive}>{(p.metadata as any)?.archived ? "Unarchive" : "Archive"}</DropdownMenu.Item>}
          </DropdownMenu.Content>
        </DropdownMenu>
      )}
    </div>
  )
}

/** Shopify's inventory column: "40 in stock", "0 in stock for 3 variants", "Inventory not tracked". */
function inventoryLabel(p: any) {
  const vs = (p.variants ?? []) as any[]
  const tracked = vs.filter((v) => v.manage_inventory)
  if (!tracked.length) return <span className="text-ui-fg-subtle">Inventory not tracked</span>
  // Available = stocked − reserved, summed over locations (Shopify's "in stock").
  const qty = tracked.reduce(
    (s, v) =>
      s +
      ((v.inventory_items?.[0]?.inventory?.location_levels ?? []) as any[]).reduce(
        (t, l) => t + Number(l.stocked_quantity ?? 0) - Number(l.reserved_quantity ?? 0),
        0
      ),
    0
  )
  return (
    <span>
      <span className={qty <= 0 ? "text-ui-fg-error" : ""}>{qty} in stock</span>
      {vs.length > 1 && <span className="text-ui-fg-subtle"> for {vs.length} variants</span>}
    </span>
  )
}

export function TagsDrawer({ noun = "product", mode, count, busy, onClose, onApply }: { noun?: string; mode: "add" | "remove"; count: number; busy: boolean; onClose: () => void; onApply: (tags: string[]) => void }) {
  const [text, setText] = useState("")
  const tags = text.split(",").map((t) => t.trim()).filter(Boolean)
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header><Drawer.Title>{mode === "add" ? `Add tags to ${count} ${noun}${count === 1 ? "" : "s"}` : `Remove tags from ${count} ${noun}${count === 1 ? "" : "s"}`}</Drawer.Title></Drawer.Header>
        <Drawer.Body><Field label="Tags" hint="Separate tags with commas"><Input value={text} onChange={(e) => setText(e.target.value)} /></Field></Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" disabled={!tags.length} isLoading={busy} onClick={() => onApply(tags)}>{mode === "add" ? "Add tags" : "Remove tags"}</Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}
